import {
  MouseEventHandler,
  SyntheticEvent,
  useEffect,
  useState,
  ReactNode
} from "react";

import { useTranslator } from "@eo-locale/react";

import pickBy from "lodash/pickBy";

import identity from "lodash/identity";

import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  CloseButton,
  Link
} from "@/storybook-components";

import { setStatus } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import EmailOrScreenNameQuestion from "../questions/email-or-screen-name-question";
import LoginPasswordQuestion from "../questions/login-password-question";
import { loginRegisteredUser, validateUser } from "@/redux/features/user/thunk";
import { resetRegisterededUser } from "@/redux/features/user/slice";

import { resendUserActivationEmail } from "@/models";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

const Form = (): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers } = useAppSelector((state) => state.questions);
  const { error, status, registeredUser } = useAppSelector(
    (state) => state.user
  );
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);

  const { status: accountStatus, id: registrationId } =
    registeredUser.accountDetails;
  const [isCodeSent, setIsCodeSent] = useState(false);

  const questions = [
    {
      id: "emailOrScreenName",
      type: "EmailOrScreenNameQuestion"
    },
    {
      id: "loginPassword",
      type: "LoginPasswordQuestion"
    }
  ];

  const isLoginQuesionsAnswered =
    questions.length !== Object.values(pickBy(answers, identity)).length;

  useEffect(() => {
    if (accountStatus === "active") {
      dispatch(loginRegisteredUser());
    }
  }, [accountStatus]);

  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();

    dispatch(resetRegisterededUser());
    dispatch(setStatus("saving"));
    await dispatch(validateUser());
    dispatch(setStatus("idle"));
    setIsCodeSent(false);
  };

  const handleClick = () => {
    resendUserActivationEmail(token, baseUrl, registrationId);
    setIsCodeSent(true);
  };

  const handleCodeSent = () => {
    setIsCodeSent(false);
    dispatch(resetRegisterededUser());
  };

  const RegisterAccountAlertNode = () => {
    if (isCodeSent) {
      return (
        <AlertNode
          status="success"
          description={translate("errors.requestNewEmailInfo")}
          onClose={handleCodeSent}
        />
      );
    }
    if (error) {
      return (
        <AlertNode
          status="error"
          title={translate("errors.invalidLoginOrPassword")}
          onClose={() => dispatch(resetRegisterededUser())}
        />
      );
    }
    if (accountStatus === "not_confirmed") {
      return (
        <AlertNode
          status="warning"
          title={translate("errors.activateYourAccount")}
          description={
            <>
              {translate("errors.activateYourAccountNote")}&nbsp;
              <Link textDecoration="underline" onClick={handleClick}>
                {translate("errors.requestNewEmail")}
              </Link>
            </>
          }
          onClose={() => dispatch(resetRegisterededUser())}
        />
      );
    }
    return null;
  };

  const AlertNode = ({
    title,
    status,
    description,
    onClose
  }: {
    title?: string;
    description?: string | ReactNode;
    onClose: MouseEventHandler<HTMLButtonElement>;
    status: "warning" | "success" | "error" | "info";
  }) => {
    return (
      <Alert status={status} alignItems={description ? "flex-start" : "center"}>
        <AlertIcon />
        <Box flex={1}>
          {title && <AlertTitle>{title}</AlertTitle>}
          {description && <AlertDescription>{description}</AlertDescription>}
        </Box>
        <CloseButton onClick={onClose} />
      </Alert>
    );
  };

  return (
    <Box as="form" noValidate onSubmit={handleSubmit}>
      <EmailOrScreenNameQuestion question={{ id: "emailOrScreenName" }} />
      <LoginPasswordQuestion question={{ id: "loginPassword" }} />
      <RegisterAccountAlertNode />
      <Button
        variant="primary"
        my={3}
        size="lg"
        type="submit"
        width="full"
        isLoading={status === "fetching"}
        isDisabled={isLoginQuesionsAnswered}
        data-testid="signIn"
      >
        {translate("login.signIn")}
      </Button>
    </Box>
  );
};

export default Form;
